import React, {FC, useEffect, useMemo, useState} from "react";
import {Button, Col, Container, FormSelect, InputGroup, Row} from "react-bootstrap";
import {MaybeDate} from "../../types/Maybe";
import {
    dateToString,
    dateWithZeroTime, getDateAsPeriod,
    getDatesInPeriod,
} from "../../tools";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar} from "@fortawesome/free-solid-svg-icons";
import RRTable from "../../components/rr-table/RRTable";
import {collection, getDocs, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../index";
import {RRCalendarDaysType} from "../../types/RRCalendarDayType";
import {calendarDataConverter} from "../../../firebase/FirebaseCalendarLoader";
import {useAppSelector} from "../../hooks";
import {selectEvents} from "../../../features/AppDataSlice";
import {selectDepartment} from "../../../features/AppStateSlice";
import {getReportMonthOptions} from "./reportMonthOptions";

const ReportNegativeRatings: FC = () => {
    const department = useAppSelector(selectDepartment);
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [working, setWorking] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>();
    const [periodTo, setPeriodTo] = useState<MaybeDate>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [report, setReport] = useState<RRCalendarDaysType | undefined>(undefined);
    const events = useAppSelector(selectEvents);

    useEffect(() => {
        if (periodFrom && periodTo) {
            const constr = [
                where("department", "==", department),
                where("eventDayEnd", ">=", periodFrom),
                where("eventDayEnd", "<", periodTo),
                where("face", "==", "BAD"),
                orderBy("eventDayEnd", "asc"),
                orderBy("employeeName", "asc")
            ];

            try {
                getDocs(query(collection(appFirestore, "calendar"), ...constr
                )).then(
                    employeesSnapshot => setReport(calendarDataConverter(employeesSnapshot.docs))
                );
            } catch (e) {
                console.error(e)
            }
        }
    }, [periodFrom, periodTo, department]);

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        const [_f, _t] = getDateAsPeriod(dpPeriodTo!);
        setPeriodTo(dateWithZeroTime(_t));
    }

    // Изборът на месец попълва началната и крайната дата с целия съответен период.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    return <Container fluid>
        <h1>Справкa за негативни оценки за период</h1>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"periodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"periodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"periodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working &&
                            <Button onClick={setRefPeriod} disabled={!dpPeriodFrom || !dpPeriodTo}>Изчисли</Button>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        <Row className={"g-2 mt-3"}>
            <Col>
                {
                    report ?
                        <RRTable>
                            <thead>
                            <tr>
                                <th className={"w-100px"}>Дата</th>
                                <th className={"w-320px"}>Служител</th>
                                <th className={"w-320px"}>Събитие</th>
                                <th>Бележки</th>
                            </tr>
                            </thead>

                            <tbody>
                            {
                                report.map(
                                    r => {
                                        const _event = events.data.find(e => e && e.id === r.eventId);
                                        return <tr key={r.id}>
                                            <td> {dateToString(r.eventDayStart)} </td>
                                            <td> {r.employeeName} </td>
                                            <td> {department === "store" ? "Кухня" : _event?.eventName} </td>
                                            <td> {r.notes} </td>
                                        </tr>
                                    }
                                )
                            }
                            </tbody>
                        </RRTable> : <strong>Моля, изберете период и натиснете "Изчисли"...</strong>
                }
            </Col>
        </Row>

    </Container>
}

export default ReportNegativeRatings;
